import { RouteName } from '@/router/models/route-name';
import router from '@/router/router';
import { notificationService } from '@/services/notification/notification-service';
import { models } from 'cesg-shared';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave } from 'vue-router';
import type {
  AtaChangelog,
  AtaDocumentPayload,
  AtaFieldKey,
  AtaFormData,
  AtaSaveCallbacks,
} from '../types';
import {
  buildAtaDownloadFileName,
  sanitizeFileNameSegment,
} from './ataDownloadFileName';
import { createAtaDetailsStore } from './createAtaDetailsStore';
import { useAtaDetailsPermissions } from './permissions/useAtaDetailsPermissions';

// --- Props ---

export interface UseAtaDetailsLogicProps {
  initialAta: AtaFormData;
  initialCreateNewAta: boolean;
  changelogs: AtaChangelog[];
  debitNormRates: Record<number, number>;
  ataId: string;
  projectId: string;
  projectFortnoxId?: string | null;
  projectName?: string | null;
  user: models.GetMeResponse | null;
  isSaving?: boolean;
  initialDocument?: Blob | null;
  isDocumentLoading?: boolean;
}

export interface UseAtaDetailsLogicEmits {
  (
    e: 'save',
    formData: AtaFormData,
    document: AtaDocumentPayload,
    callbacks: AtaSaveCallbacks,
  ): void;
}

export const useAtaDetailsLogic = (
  props: UseAtaDetailsLogicProps,
  emit: UseAtaDetailsLogicEmits,
) => {
  const { t } = useI18n();

  const { formData, createNewAta, dispatch, isDirty } = createAtaDetailsStore(
    props.initialAta,
    props.initialCreateNewAta,
  );

  const documentFile = ref<File | null>(null);
  const documentDirty = ref(false);

  const permissions = useAtaDetailsPermissions({
    status: computed(() => formData.value.status),
    user: props.user,
    projectId: props.projectId,
  });

  watch(
    () => props.initialAta,
    (data) => {
      dispatch({
        type: 'load',
        data,
        createNewAta: props.initialCreateNewAta,
      });
    },
  );

  function buildPrintUrl(): string {
    return router.resolve({
      name: RouteName.ATADetailsPrint,
      params: { ataId: props.ataId, id: props.projectId },
    }).href;
  }

  function goToList(): Promise<unknown> {
    return router.push({
      name: RouteName.ATA,
      params: { id: props.projectId },
    });
  }

  async function save(): Promise<boolean> {
    const emptyAtaAccounts = formData.value.costs.filter(
      (cost) => !cost.accountNumber || cost.accountNumber < 1,
    );

    if (emptyAtaAccounts.length > 0) {
      notificationService.open({
        text: t('notifications.noAccountInCostRow'),
        type: 'danger',
      });

      return false;
    }

    dispatch({ type: 'stamp-revision-date' });
    const wasCreate = createNewAta.value;

    const persisted = await new Promise<boolean>((resolve) => {
      emit(
        'save',
        formData.value,
        { file: documentFile.value, dirty: documentDirty.value },
        {
          onSuccess: () => resolve(true),
          onError: () => resolve(false),
        },
      );
    });

    if (!persisted) return false;

    dispatch({ type: 'reset-baseline' });
    documentFile.value = null;
    documentDirty.value = false;

    if (wasCreate) {
      await goToList();
      return false;
    }

    return true;
  }

  async function printAta(): Promise<void> {
    if (isDirty.value) {
      const printWindow = window.open('', '_blank');

      const saved = await save();
      if (!saved) {
        printWindow?.close();
        return;
      }

      if (printWindow) {
        printWindow.location.href = buildPrintUrl();
        return;
      }
    }

    window.open(buildPrintUrl(), '_blank');
  }

  const documentFileName = computed(() => {
    const title = (formData.value.title ?? '').trim();
    const safe = sanitizeFileNameSegment(`${props.projectId}-ÄTA-${title}`);
    return `${safe}.pdf`;
  });

  const downloadFileName = computed(() =>
    buildAtaDownloadFileName({
      fortnoxId: props.projectFortnoxId,
      projectName: props.projectName,
      serialNumber: formData.value.serialNumber,
    }),
  );

  onBeforeRouteLeave((to, from, next) => {
    if (isDirty.value) {
      const confirmLeave = window.confirm(t('unsavedChangesNotification'));
      next(confirmLeave);
    } else {
      next();
    }
  });

  function setDocument(file: File | null): void {
    documentFile.value = file;
    documentDirty.value = true;
  }

  const action = {
    updateField: (field: AtaFieldKey, value: unknown) =>
      dispatch({ type: 'update-field', field, value }),
    setStatus: (status: models.domain.AtaStatusType) =>
      dispatch({ type: 'set-status', status }),
    setDocument,
    save,
    print: printAta,
  };

  const state = computed(() => ({
    formData: formData.value,
    changelogs: props.changelogs,
    debitNormRates: props.debitNormRates,
    permissions: permissions.value,
    readOnly: permissions.value.readOnly,
    isDirty: isDirty.value || documentDirty.value,
    isSaving: props.isSaving ?? false,
    isCreate: createNewAta.value,
    documentFileName: documentFileName.value,
    downloadFileName: downloadFileName.value,
    hasStagedDocument: documentFile.value !== null,
    isDocumentLoading: props.isDocumentLoading ?? false,
  }));

  return {
    state,
    action,
    formData,
    readOnly: computed(() => permissions.value.readOnly),
  };
};
